import { toast } from 'sonner'
import { findNodeDefinitionByFile } from '@/shared/lib/node-registry'
import { formatFileSize, VIDEO_EXTENSIONS, AUDIO_EXTENSIONS } from '@/shared/config/file-restrictions'

/**
 * 通用文件校验函数：
 * - 动态查找对应的 Node Definition。
 * - 校验 Node Definition 配置中定义的 maxSize 和 maxDuration。
 */
export async function validateMediaFile(file: File): Promise<{ isValid: boolean; error?: string }> {
  const type = file.type

  // 1. 通过统一的注册表匹配节点定义
  const definition = findNodeDefinitionByFile(file)
  if (!definition) {
    // 文本文件特殊放行，因为它们可能不在 create.fileAccepts 中
    if (type.startsWith('text/') || type === 'application/json' || type === 'text/csv' || file.name.endsWith('.md')) {
      return { isValid: true }
    }
    
    const allowedExts = type.startsWith('video/') ? VIDEO_EXTENSIONS
      : type.startsWith('audio/') ? AUDIO_EXTENSIONS
        : ['.jpeg', '.jpg', '.png']
        
    return { isValid: false, error: `推荐上传 ${allowedExts.join('、')} 格式` }
  }

  const createCap = definition.create
  if (!createCap) return { isValid: true }

  // 2. 动态读取节点定义中的 maxSize 限制进行拦截
  if (createCap.maxSize && file.size > createCap.maxSize) {
    return { isValid: false, error: `请上传小于${formatFileSize(createCap.maxSize)}的文件` }
  }

  // 3. 动态读取 maxDuration 进行音视频时长拦截
  if (createCap.maxDuration && (type.startsWith('video/') || type.startsWith('audio/'))) {
    const duration = await new Promise<number>((resolve) => {
      const media = document.createElement(type.startsWith('video/') ? 'video' : 'audio') as HTMLMediaElement
      media.preload = 'metadata'
      media.onloadedmetadata = () => {
        window.URL.revokeObjectURL(media.src)
        resolve(media.duration)
      }
      media.onerror = () => {
        window.URL.revokeObjectURL(media.src)
        resolve(0)
      }
      media.src = window.URL.createObjectURL(file)
    })

    if (duration > createCap.maxDuration) {
      return { isValid: false, error: `时长不能超过${Math.floor(createCap.maxDuration / 60)}分钟` }
    }
  }

  return { isValid: true }
}

/**
 * 批量校验文件集合。
 * 返回校验通过的文件数组。不通过的文件内部会触发 toast 提示并被过滤掉。
 */
export async function validateFiles(files: File[]): Promise<File[]> {
  const results = await Promise.all(
    files.map(async (file) => {
      const result = await validateMediaFile(file)
      return { file, ...result }
    })
  )

  const invalidResults = results.filter((r) => !r.isValid)
  
  if (invalidResults.length > 0) {
    if (invalidResults.length === 1) {
      const result = invalidResults[0]
      if (result.error?.includes('推荐上传')) {
        toast.warning(`${result.file.name} 文件上传失败，${result.error}`)
      } else {
        toast.error(result.error)
      }
    } else {
      const names = invalidResults.map(r => r.file.name).join('、')
      toast.warning(`以下文件上传失败，已跳过：${names}`)
    }
  }

  return results.filter((r) => r.isValid).map((r) => r.file)
}
